import React, { useState, useEffect } from "react";
import NotificationBell from "./NotificationBell";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingBag, Layers, ShoppingCart, Users,
  Shield, Settings, Menu, LogOut,
  FileText, ChevronLeft, ChevronRight, X, Image, Inbox, TicketIcon, Bell, CalendarDays,
  Package, Gift, Award, MapPin, UserCheck, Repeat, Receipt, ScrollText, Share2, Link2, Star,
  UtensilsCrossed, Languages, Monitor, ChefHat, Mail, MessageSquare,
  RotateCcw, Truck, BookOpen, Navigation,
} from "lucide-react";
import { useAppContext } from "../../context";
import { useAdmin } from "./admin-context";

// ── Single nav link ────────────────────────────────────────────
const NavItem = ({ to, icon: Icon, label, active, collapsed, onNavigate, badge }) =>
  collapsed ? (
    <Link
      to={to}
      onClick={onNavigate}
      title={label}
      className={`relative w-9 h-9 mx-auto flex items-center justify-center rounded-lg transition-all duration-150 group
        ${active ? "bg-orange-500 text-white shadow-md shadow-orange-900/30" : "text-slate-400 hover:bg-slate-700/60 hover:text-white"}`}
    >
      <Icon size={16} />
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center ring-1 ring-slate-800">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
      <div className="pointer-events-none absolute left-full ml-2.5 px-2.5 py-1.5 bg-slate-900 border border-slate-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 transition-opacity shadow-xl">
        {label}
      </div>
    </Link>
  ) : (
    <Link
      to={to}
      onClick={onNavigate}
      className={`flex items-center gap-2.5 px-2.5 py-[7px] rounded-lg transition-all duration-150
        ${active ? "bg-orange-500 text-white shadow-sm shadow-orange-900/20" : "text-slate-300 hover:bg-slate-700/50 hover:text-white"}`}
    >
      <Icon size={14} className="flex-shrink-0 opacity-80" />
      <span className="text-[12.5px] font-medium truncate flex-1 leading-none">{label}</span>
      {badge > 0 && (
        <span className="bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none flex-shrink-0 ring-1 ring-white/20">
          {badge}
        </span>
      )}
    </Link>
  );

// ── Accordion group (expanded only) ───────────────────────────
const NavGroup = ({ group, location, onNavigate, isOpen, onToggle }) => {
  const hasActive = group.items.some((i) => location.pathname === i.path);
  const GroupIcon = group.icon;

  return (
    <div>
      <button
        onClick={onToggle}
        type="button"
        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-150 group
          ${hasActive
            ? "text-orange-400"
            : "text-slate-400 hover:text-white hover:bg-slate-700/40"
          }`}
      >
        <GroupIcon size={15} className="flex-shrink-0" />
        <span className="text-[11px] font-bold uppercase tracking-wider flex-1 text-left leading-none">
          {group.label}
        </span>
        {group.totalBadge > 0 && !isOpen && (
          <span className="bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
            {group.totalBadge}
          </span>
        )}
        <ChevronRight
          size={13}
          className={`flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="ml-3 pl-2.5 border-l border-slate-700 mt-0.5 mb-1 space-y-0.5">
          {group.items.map((item) => (
            <NavItem
              key={item.path}
              to={item.path}
              icon={item.icon}
              label={item.label}
              active={location.pathname === item.path}
              collapsed={false}
              onNavigate={onNavigate}
              badge={item.badge}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Sidebar content (shared between mobile + desktop) ─────────
const SidebarInner = ({ collapsed, groups, location, logoutAdmin, navigate, onNavigate }) => {
  const activeGroupIndex = groups.findIndex((g) =>
    g.items.some((i) => location.pathname === i.path)
  );
  const [openIndex, setOpenIndex] = useState(activeGroupIndex >= 0 ? activeGroupIndex : 0);

  // When route changes, open the group containing the active item
  useEffect(() => {
    const idx = groups.findIndex((g) => g.items.some((i) => location.pathname === i.path));
    if (idx >= 0) setOpenIndex(idx);
  }, [location.pathname, groups]);

  const toggle = (i) => setOpenIndex((prev) => (prev === i ? -1 : i));

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <nav className="flex-1 overflow-y-auto py-2 px-2 scrollbar-thin space-y-0.5">
        {collapsed
          ? /* collapsed: flat icon list with thin dividers between groups */
            groups.map((group, gi) => (
              <div key={gi}>
                <div className="flex flex-col items-center gap-0.5">
                  {group.items.map((item) => (
                    <NavItem
                      key={item.path}
                      to={item.path}
                      icon={item.icon}
                      label={item.label}
                      active={location.pathname === item.path}
                      collapsed
                      onNavigate={onNavigate}
                      badge={item.badge}
                    />
                  ))}
                </div>
                {gi < groups.length - 1 && (
                  <div className="mx-auto my-1.5 w-5 border-t border-slate-700/60" />
                )}
              </div>
            ))
          : /* expanded: accordion groups */
            groups.map((group, gi) => (
              <NavGroup
                key={gi}
                group={group}
                location={location}
                onNavigate={onNavigate}
                isOpen={openIndex === gi}
                onToggle={() => toggle(gi)}
              />
            ))
        }
      </nav>

      {/* Footer actions */}
      <div className={`flex-shrink-0 pb-3 pt-2 border-t border-slate-700 px-2 space-y-0.5`}>
        <button
          onClick={() => { logoutAdmin(); navigate("/admin", { replace: true }); }}
          title={collapsed ? "Sign Out" : undefined}
          className={`relative group transition-all duration-150 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-lg
            ${collapsed ? "w-9 h-9 mx-auto flex items-center justify-center" : "flex items-center gap-2.5 w-full px-2.5 py-2"}`}
        >
          <LogOut size={16} className="flex-shrink-0" />
          {!collapsed && <span className="text-[13px] font-medium">Sign Out</span>}
          {collapsed && (
            <div className="pointer-events-none absolute left-full ml-2.5 px-2.5 py-1.5 bg-slate-900 border border-slate-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 transition-opacity shadow-xl">
              Sign Out
            </div>
          )}
        </button>
        <Link
          to="/"
          onClick={onNavigate}
          title={collapsed ? "Back to Store" : undefined}
          className={`relative group transition-all duration-150 text-slate-400 hover:bg-slate-700/60 hover:text-white rounded-lg
            ${collapsed ? "w-9 h-9 mx-auto flex items-center justify-center" : "flex items-center gap-2.5 w-full px-2.5 py-2"}`}
        >
          <ShoppingCart size={16} className="flex-shrink-0" />
          {!collapsed && <span className="text-[13px] font-medium">Back to Store</span>}
          {collapsed && (
            <div className="pointer-events-none absolute left-full ml-2.5 px-2.5 py-1.5 bg-slate-900 border border-slate-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 transition-opacity shadow-xl">
              Back to Store
            </div>
          )}
        </Link>
      </div>
    </div>
  );
};

// ── Layout ────────────────────────────────────────────────────
const AdminLayout = () => {
  const [collapsed, setCollapsed]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location  = useLocation();
  const navigate  = useNavigate();
  const { adminUser, logoutAdmin, storeSettings } = useAppContext();
  const { hasPermission, contactSubmissions, tickets } = useAdmin();
  const storeName       = storeSettings?.store_name || "Gamapa Foods";
  const unreadContacts  = (contactSubmissions || []).filter(c => c.status === "new").length;
  const openTickets     = (tickets || []).filter(t => t.status === "open").length;

  const allGroups = [
    {
      label: "Main",
      icon: LayoutDashboard,
      items: [
        { path: "/admin",              icon: LayoutDashboard, label: "Dashboard",       permission: "dashboard.view" },
        { path: "/admin/orders",       icon: ShoppingCart,    label: "Orders",          permission: "orders.view"    },
        { path: "/admin/reservations", icon: CalendarDays,    label: "Reservations",    permission: "orders.view"    },
        { path: "/admin/pos",          icon: Monitor,         label: "Point of Sale",   permission: "orders.edit"    },
        { path: "/admin/kitchen",      icon: ChefHat,         label: "Kitchen Display", permission: "orders.view"    },
      ],
    },
    {
      label: "Catalog",
      icon: ShoppingBag,
      items: [
        { path: "/admin/products",     icon: ShoppingBag,     label: "Products",    permission: "products.view"   },
        { path: "/admin/categories",   icon: Layers,          label: "Categories",  permission: "categories.view" },
        { path: "/admin/inventory",    icon: Package,         label: "Inventory",   permission: "products.view"   },
        { path: "/admin/menu-builder", icon: UtensilsCrossed, label: "Menu Builder",permission: "products.view"   },
      ],
    },
    {
      label: "Customers",
      icon: Users,
      items: [
        { path: "/admin/customers",     icon: Users,     label: "Customers",        permission: "customers.view" },
        { path: "/admin/loyalty",       icon: Award,     label: "Loyalty",          permission: "customers.view" },
        { path: "/admin/gift-cards",    icon: Gift,      label: "Gift Cards",       permission: "settings.view"  },
        { path: "/admin/reviews",       icon: Star,      label: "Reviews",          permission: "settings.view"  },
        { path: "/admin/subscriptions", icon: Repeat,    label: "Subscriptions",    permission: "orders.view"    },
        { path: "/admin/returns",       icon: RotateCcw, label: "Returns & Refunds",permission: "orders.view"    },
      ],
    },
    {
      label: "Content",
      icon: FileText,
      items: [
        { path: "/admin/pages",        icon: FileText,   label: "Pages",        permission: "pages.view"    },
        { path: "/admin/blog",         icon: FileText,   label: "Blog",         permission: "blog.view"     },
        { path: "/admin/media",        icon: Image,      label: "Media",        permission: "settings.view" },
        { path: "/admin/translations", icon: Languages,  label: "Translations", permission: "settings.view" },
        { path: "/admin/nav-links",    icon: Navigation, label: "Nav Links",    permission: "settings.view" },
      ],
    },
    {
      label: "Marketing",
      icon: Mail,
      items: [
        { path: "/admin/emails",              icon: Mail,          label: "Email Campaigns",    permission: "settings.view" },
        { path: "/admin/sms",                 icon: MessageSquare, label: "SMS Campaigns",      permission: "settings.view" },
        { path: "/admin/push-notifications",  icon: Bell,          label: "Push Notifications", permission: "settings.view" },
        { path: "/admin/social-media",        icon: Share2,        label: "Social Media",       permission: "settings.view" },
        { path: "/admin/affiliates",          icon: Link2,         label: "Affiliates",         permission: "settings.view" },
        { path: "/admin/events",              icon: CalendarDays,  label: "Events",             permission: "settings.view" },
      ],
    },
    {
      label: "Operations",
      icon: Truck,
      items: [
        { path: "/admin/staff",          icon: UserCheck, label: "Staff",          permission: "customers.view" },
        { path: "/admin/vendors",        icon: Truck,     label: "Vendors",        permission: "products.view"  },
        { path: "/admin/delivery-zones", icon: MapPin,    label: "Delivery Zones", permission: "settings.view"  },
        { path: "/admin/tax-rules",      icon: Receipt,   label: "Tax Rules",      permission: "settings.view"  },
        { path: "/admin/careers",        icon: BookOpen,  label: "Careers",        permission: "settings.view"  },
      ],
    },
    {
      label: "System",
      icon: Settings,
      items: [
        { path: "/admin/contacts",       icon: Inbox,      label: "Contacts",          permission: "settings.view", badge: unreadContacts },
        { path: "/admin/tickets",        icon: TicketIcon, label: "Tickets",           permission: "settings.view", badge: openTickets    },
        { path: "/admin/notifications",  icon: Bell,       label: "Notifications",     permission: "settings.view" },
        { path: "/admin/roles",          icon: Shield,     label: "User Roles",        permission: "roles.view"    },
        { path: "/admin/audit-log",      icon: ScrollText, label: "Audit Log",         permission: "settings.view" },
        { path: "/admin/settings",       icon: Settings,   label: "Settings",          permission: "settings.view" },
      ],
    },
  ];

  const groups = allGroups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => hasPermission(item.permission)),
      totalBadge: g.items.reduce((sum, i) => sum + (i.badge || 0), 0),
    }))
    .filter((g) => g.items.length > 0);

  const sidebarW   = collapsed ? "w-14"     : "w-56";
  const contentML  = collapsed ? "md:ml-14" : "md:ml-56";
  const headerLeft = collapsed ? "md:left-14" : "md:left-56";

  const Logo = ({ showToggle, onClose }) => (
    <div className="h-14 flex items-center flex-shrink-0 border-b border-slate-700 px-3 gap-2.5">
      <div className="bg-orange-500 p-1.5 rounded-lg flex-shrink-0">
        <ShoppingCart size={16} className="text-white" />
      </div>
      {!collapsed && (
        <span className="text-sm font-extrabold text-white tracking-tight truncate flex-1">
          {storeName.toUpperCase()}
        </span>
      )}
      {showToggle && !collapsed && (
        <button
          onClick={() => setCollapsed(true)}
          className="hidden md:flex p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors flex-shrink-0"
          title="Collapse sidebar"
        >
          <ChevronLeft size={14} />
        </button>
      )}
      {onClose && (
        <button
          onClick={onClose}
          className="md:hidden p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors flex-shrink-0"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );

  return (
    <div className="h-screen overflow-hidden bg-gray-50">

      {/* ── Mobile backdrop ───────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Mobile sidebar (drawer) ────────────────────────────── */}
      <aside className={`fixed top-0 left-0 h-full w-56 bg-slate-800 z-50 flex flex-col transition-transform duration-300 ease-in-out md:hidden
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <Logo showToggle={false} onClose={() => setMobileOpen(false)} />
        <SidebarInner
          collapsed={false}
          groups={groups}
          location={location}
          logoutAdmin={logoutAdmin}
          navigate={navigate}
          onNavigate={() => setMobileOpen(false)}
        />
      </aside>

      {/* ── Desktop sidebar ────────────────────────────────────── */}
      <aside className={`hidden md:flex fixed top-0 left-0 h-full ${sidebarW} bg-slate-800 z-50 flex-col transition-all duration-300 ease-in-out overflow-hidden`}>
        {collapsed ? (
          <div className="h-14 flex flex-col items-center justify-center gap-1 border-b border-slate-700 flex-shrink-0">
            <div className="bg-orange-500 p-1.5 rounded-lg">
              <ShoppingCart size={14} className="text-white" />
            </div>
            <button
              onClick={() => setCollapsed(false)}
              className="p-1 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              title="Expand sidebar"
            >
              <ChevronRight size={12} />
            </button>
          </div>
        ) : (
          <Logo showToggle={true} onClose={null} />
        )}
        <SidebarInner
          collapsed={collapsed}
          groups={groups}
          location={location}
          logoutAdmin={logoutAdmin}
          navigate={navigate}
          onNavigate={null}
        />
      </aside>

      {/* ── Topbar ─────────────────────────────────────────────── */}
      <header className={`fixed top-0 left-0 ${headerLeft} right-0 h-14 bg-white border-b z-30 flex items-center justify-between px-4 sm:px-6 transition-all duration-300 ease-in-out`}>
        <button
          onClick={() => setMobileOpen(true)}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <Menu size={18} />
        </button>

        <div className="flex items-center gap-3 ml-auto">
          <NotificationBell />
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold text-gray-900">{adminUser?.firstName} {adminUser?.lastName}</p>
            <p className="text-xs text-gray-500 capitalize">{adminUser?.role?.name || "Admin"}</p>
          </div>
          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold text-sm ring-2 ring-orange-200">
            {adminUser?.firstName?.[0] || "A"}
          </div>
        </div>
      </header>

      {/* ── Content ────────────────────────────────────────────── */}
      <div className={`${contentML} pt-14 h-full transition-all duration-300 ease-in-out`}>
        <main className="h-full overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

    </div>
  );
};

export default AdminLayout;
